import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { usePaymentApprovals, useRefundApprovals, usePaymentHistory } from '@/hooks/usePaymentApprovals';
import { usePlatformSetting } from '@/hooks/usePlatformSettings';
import { useToast } from '@/hooks/use-toast';
import { TransactionBreakdown } from '@/components/payments/TransactionBreakdown';
import { PaymentProcessingModal } from '@/components/milestones/PaymentProcessingModal';
import { JobLedgerView } from '@/components/admin/JobLedgerView';
import { FailedPayoutsSection } from '@/components/admin/FailedPayoutsSection';
import { CheckCircle, XCircle, Clock, DollarSign, AlertTriangle, FileText, Building, RefreshCw, ClipboardCheck } from 'lucide-react';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export default function PaymentApprovalsPage() {
  const [selectedTab, setSelectedTab] = useState('payouts');
  
  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Payment Approvals</h1>
        <p className="text-muted-foreground">Review and approve vendor payouts and client refunds</p>
      </div>

      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList>
          <TabsTrigger value="payouts">Vendor Payouts</TabsTrigger>
          <TabsTrigger value="refunds">Client Refunds</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="payouts" className="space-y-6">
          <FailedPayoutsSection />
          <VendorPayoutsSection />
        </TabsContent>

        <TabsContent value="refunds" className="space-y-4">
          <ClientRefundsSection />
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <PaymentHistorySection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function VendorPayoutsSection() {
  const { data: pendingPayouts, isLoading, refetch, isFetching } = usePaymentApprovals('pending_approval');
  const { approvePayoutMutation, rejectPayoutMutation, manualCompletePayoutMutation } = usePaymentApprovals();
  const { data: platformFeeSetting } = usePlatformSetting('platform_fee_percentage');
  const configuredFeePercentage = parseFloat(platformFeeSetting?.setting_value || '7');
  const [selectedPayout, setSelectedPayout] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [expandedTransactions, setExpandedTransactions] = useState<Record<string, boolean>>({});
  const { toast } = useToast();
  
  // Payment processing modal state
  const [showPaymentProcessingModal, setShowPaymentProcessingModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [paymentError, setPaymentError] = useState('');
  const [transferId, setTransferId] = useState('');
  const [processingQueueId, setProcessingQueueId] = useState<string | null>(null);
  
  // Manual completion dialog state
  const [showManualCompleteDialog, setShowManualCompleteDialog] = useState(false);
  const [manualCompleteQueueId, setManualCompleteQueueId] = useState<string | null>(null);
  const [manualReason, setManualReason] = useState('');

  if (isLoading) {
    return <div>Loading payouts...</div>;
  }

  if (!pendingPayouts || pendingPayouts.length === 0) {
    return (
      <Alert>
        <Clock className="h-4 w-4" />
        <AlertDescription>No pending vendor payouts require approval at this time.</AlertDescription>
      </Alert>
    );
  }

  const handleApprove = (queueId: string) => {
    setProcessingQueueId(queueId);
    setPaymentStatus('processing');
    setPaymentError('');
    setTransferId('');
    
    approvePayoutMutation.mutate({ 
      queueId, 
      notes,
      callbacks: {
        onTransferStart: (amount) => {
          setPaymentAmount(amount);
          setShowPaymentProcessingModal(true);
        },
        onTransferSuccess: (transferId) => {
          setPaymentStatus('success');
          setTransferId(transferId);
          // Auto-close after 2 seconds
          setTimeout(() => {
            setShowPaymentProcessingModal(false);
            setSelectedPayout(null);
            setNotes('');
            setProcessingQueueId(null);
          }, 2000);
        },
        onTransferError: (error) => {
          setPaymentStatus('error');
          setPaymentError(error);
        }
      }
    });
  };

  const handleRetry = () => {
    if (processingQueueId) {
      handleApprove(processingQueueId);
    }
  };

  const handleReject = (queueId: string) => {
    if (!notes.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    rejectPayoutMutation.mutate({ queueId, rejectionReason: notes });
    setSelectedPayout(null);
    setNotes('');
  };

  const handleManualComplete = (queueId: string) => {
    setManualCompleteQueueId(queueId);
    setManualReason('');
    setShowManualCompleteDialog(true);
  };

  const confirmManualComplete = () => {
    if (!manualCompleteQueueId || !manualReason.trim()) return;
    
    manualCompletePayoutMutation.mutate({
      queueId: manualCompleteQueueId,
      notes,
      reason: manualReason,
    }, {
      onSuccess: () => {
        setShowManualCompleteDialog(false);
        setManualCompleteQueueId(null);
        setManualReason('');
        setSelectedPayout(null);
        setNotes('');
      }
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <Alert className="flex-1">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Important:</strong> Review each payout carefully before approval. Verify amounts, vendor details, and job completion status.
          </AlertDescription>
        </Alert>
        <Button
          variant="outline"
          size="sm"
          onClick={async () => {
            console.log('Refetch button clicked');
            const result = await refetch();
            console.log('Refetch result:', result);
            toast({
              title: 'Data Refreshed',
              description: 'Payment approvals data has been reloaded from the database.',
            });
          }}
          disabled={isFetching}
          className="whitespace-nowrap"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          {isFetching ? 'Refreshing...' : 'Refetch Data'}
        </Button>
      </div>

      {pendingPayouts.map((payout) => {
        const job = payout.jobs;
        const vendorPayoutCents = payout.display_vendor_payout_cents || payout.aggregated_vendor_payout_cents || 0;
        const totalAmountCents = payout.display_total_amount_cents || payout.aggregated_total_amount_cents || 0;
        // Use aggregated platform fee from transactions (correctly calculated as 7% of base)
        const platformFeeCents = payout.display_platform_fee_cents || payout.aggregated_platform_fee_cents || 0;
        const transactionCount = payout.transaction_count || 1;
        const isExpanded = expandedTransactions[payout.id] || false;
        const hasAdjustments = payout.has_reconciliation && payout.reconciliation;

        // Debug logging for each payout
        console.log(`🎯 [UI RENDER] ${job?.public_id}:`, {
          queueId: payout.id,
          has_reconciliation: payout.has_reconciliation,
          display_vendor_payout_cents: payout.display_vendor_payout_cents,
          aggregated_vendor_payout_cents: payout.aggregated_vendor_payout_cents,
          display_total_amount_cents: payout.display_total_amount_cents,
          aggregated_total_amount_cents: payout.aggregated_total_amount_cents,
          vendorPayoutCents_computed: vendorPayoutCents,
          totalAmountCents_computed: totalAmountCents,
          reconciliation: payout.reconciliation,
        });

        return (
          <Card key={payout.id} className="border-l-4 border-l-warning">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Vendor Payout - {job?.public_id}
                    {transactionCount > 1 && (
                      <Badge variant="secondary" className="ml-2">
                        {transactionCount} payments
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription>{job?.title}</CardDescription>
                </div>
                <Badge variant="outline" className="bg-warning/10">
                  <Clock className="h-3 w-3 mr-1" />
                  Pending Approval
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {hasAdjustments && (
                <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="secondary">Reconciliation Applied</Badge>
                    {payout.reconciliation!.adjustment_count > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {payout.reconciliation!.adjustment_count} change order{payout.reconciliation!.adjustment_count > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Original PO:</span>
                      <span>${(payout.reconciliation!.original_po_amount_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {payout.reconciliation!.total_increases_cents > 0 && (
                      <div className="flex justify-between text-green-600 dark:text-green-400">
                        <span>Increases:</span>
                        <span>+${(payout.reconciliation!.total_increases_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {payout.reconciliation!.total_credits_cents > 0 && (
                      <div className="flex justify-between text-red-600 dark:text-red-400">
                        <span>Credits:</span>
                        <span>-${(payout.reconciliation!.total_credits_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-medium border-t pt-2">
                      <span>Final PO (Base, excl. tax):</span>
                      <span>${(payout.reconciliation!.final_po_amount_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-muted-foreground">
                    Vendor Payout Amount (After Platform Fee)
                  </p>
                  {transactionCount > 1 && !hasAdjustments && (
                    <Badge variant="secondary">{transactionCount} transactions aggregated</Badge>
                  )}
                </div>
                <p className="text-3xl font-bold text-primary">
                  ${(vendorPayoutCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Platform Fee ({configuredFeePercentage}%): ${(platformFeeCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  This is the {hasAdjustments ? 'adjusted ' : ''}amount that will be transferred to the vendor
                </p>
              </div>

              {transactionCount > 1 && (
                <TransactionBreakdown jobId={payout.job_id} isExpanded={isExpanded} onToggle={() => setExpandedTransactions(prev => ({ ...prev, [payout.id]: !isExpanded }))} />
              )}

              {/* Job Ledger View */}
              <JobLedgerView jobId={payout.job_id} />

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-muted-foreground">Payment Status</p>
                  <p className="font-medium">{job?.payment_status}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Requested</p>
                  <p className="font-medium">{format(new Date(payout.created_at), 'PPp')}</p>
                </div>
              </div>

              {selectedPayout === payout.id ? (
                <div className="space-y-3 pt-4 border-t">
                  <Textarea
                    placeholder="Add approval notes or rejection reason..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="default"
                      onClick={() => handleApprove(payout.id)}
                      disabled={approvePayoutMutation.isPending}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve Payout
                    </Button>
                    <Button
                      variant="outline"
                      className="border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                      onClick={() => handleManualComplete(payout.id)}
                      disabled={manualCompletePayoutMutation.isPending}
                    >
                      <ClipboardCheck className="h-4 w-4 mr-2" />
                      Manually Completed
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleReject(payout.id)}
                      disabled={rejectPayoutMutation.isPending}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedPayout(null);
                        setNotes('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 pt-4 border-t">
                  <Button onClick={() => setSelectedPayout(payout.id)}>
                    Review & Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(`/jobs/${job?.id}`, '_blank')}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View Job
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <PaymentProcessingModal
        open={showPaymentProcessingModal}
        amount={paymentAmount}
        status={paymentStatus}
        errorMessage={paymentError}
        transactionId={transferId}
        onClose={() => {
          setShowPaymentProcessingModal(false);
          setSelectedPayout(null);
          setNotes('');
          setProcessingQueueId(null);
        }}
        onRetry={handleRetry}
      />

      {/* Manual Complete Dialog for Payouts */}
      <Dialog open={showManualCompleteDialog} onOpenChange={setShowManualCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Payout as Manually Completed</DialogTitle>
            <DialogDescription>
              This will mark the payout as completed without processing through Stripe. 
              The vendor will NOT receive funds through the platform. Use this only when 
              the payment was processed manually (e.g., bank wire transfer).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="manual-reason">Completion Reason (Required)</Label>
              <Textarea
                id="manual-reason"
                placeholder="e.g., Processed via bank wire transfer on 01/15/2026, confirmation #ABC123"
                value={manualReason}
                onChange={(e) => setManualReason(e.target.value)}
                rows={3}
              />
            </div>
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Important:</strong> This action will update the job status to "Closed" 
                and payment status to "Paid to Vendor" just like a normal Stripe payout would.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualCompleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmManualComplete}
              disabled={!manualReason.trim() || manualCompletePayoutMutation.isPending}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {manualCompletePayoutMutation.isPending ? 'Processing...' : 'Confirm Manual Completion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClientRefundsSection() {
  const { data: pendingRefunds, isLoading } = useRefundApprovals('pending');
  const { approveRefundMutation, rejectRefundMutation, manualCompleteRefundMutation } = useRefundApprovals();
  const [selectedRefund, setSelectedRefund] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  
  // Payment processing modal state
  const [showRefundProcessingModal, setShowRefundProcessingModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundStatus, setRefundStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [refundError, setRefundError] = useState('');
  const [stripeRefundId, setStripeRefundId] = useState('');
  const [processingRefundId, setProcessingRefundId] = useState<string | null>(null);
  
  // Manual completion dialog state
  const [showManualCompleteDialog, setShowManualCompleteDialog] = useState(false);
  const [manualCompleteRefundId, setManualCompleteRefundId] = useState<string | null>(null);
  const [manualReason, setManualReason] = useState('');

  if (isLoading) {
    return <div>Loading refunds...</div>;
  }

  if (!pendingRefunds || pendingRefunds.length === 0) {
    return (
      <Alert>
        <Clock className="h-4 w-4" />
        <AlertDescription>No pending client refunds require approval at this time.</AlertDescription>
      </Alert>
    );
  }

  const handleApprove = (refundId: string) => {
    setProcessingRefundId(refundId);
    setRefundStatus('processing');
    setRefundError('');
    setStripeRefundId('');
    
    approveRefundMutation.mutate({ 
      refundId, 
      notes,
      callbacks: {
        onRefundStart: (amount) => {
          setRefundAmount(amount);
          setShowRefundProcessingModal(true);
        },
        onRefundSuccess: (stripeRefundId) => {
          setRefundStatus('success');
          setStripeRefundId(stripeRefundId);
          // Auto-close after 2 seconds
          setTimeout(() => {
            setShowRefundProcessingModal(false);
            setSelectedRefund(null);
            setNotes('');
            setProcessingRefundId(null);
          }, 2000);
        },
        onRefundError: (error) => {
          setRefundStatus('error');
          setRefundError(error);
        }
      }
    });
  };

  const handleRetryRefund = () => {
    if (processingRefundId) {
      handleApprove(processingRefundId);
    }
  };

  const handleReject = (refundId: string) => {
    if (!notes.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    rejectRefundMutation.mutate({ refundId, rejectionReason: notes });
    setSelectedRefund(null);
    setNotes('');
  };

  const handleManualComplete = (refundId: string) => {
    setManualCompleteRefundId(refundId);
    setManualReason('');
    setShowManualCompleteDialog(true);
  };

  const confirmManualComplete = () => {
    if (!manualCompleteRefundId || !manualReason.trim()) return;
    
    manualCompleteRefundMutation.mutate({
      refundId: manualCompleteRefundId,
      notes,
      reason: manualReason,
    }, {
      onSuccess: () => {
        setShowManualCompleteDialog(false);
        setManualCompleteRefundId(null);
        setManualReason('');
        setSelectedRefund(null);
        setNotes('');
      }
    });
  };

  return (
    <div className="space-y-4">
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <strong>Important:</strong> Verify refund amounts against original payments and adjustments before approval.
        </AlertDescription>
      </Alert>

      {pendingRefunds.map((refund) => {
        const job = refund.jobs;
        return (
        <Card key={refund.id} className="border-l-4 border-l-info">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Client Refund - {job?.public_id || 'N/A'}
                </CardTitle>
                <CardDescription>{job?.title || refund.refund_type}</CardDescription>
              </div>
              <Badge variant="outline" className="bg-info/10">
                <Clock className="h-3 w-3 mr-1" />
                Pending Approval
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Job Number</p>
                <p className="font-medium">{job?.public_id || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Job Name</p>
                <p className="font-medium">{job?.title || 'N/A'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Refund Amount</p>
                <p className="text-xl font-bold text-primary">
                  ${(refund.refund_amount_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Reason</p>
                <p className="font-medium">{
                  refund.reason?.replace(/\$[\d.]+/g, (match) => {
                    const num = parseFloat(match.substring(1));
                    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  })
                }</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Requested</p>
                <p className="font-medium">{format(new Date(refund.requested_at), 'PPp')}</p>
              </div>
            </div>

            {/* Job Ledger View */}
            <JobLedgerView jobId={refund.job_id} />

            {selectedRefund === refund.id ? (
              <div className="space-y-3 pt-4 border-t">
                <Textarea
                  placeholder="Add approval notes or rejection reason..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="default"
                    onClick={() => handleApprove(refund.id)}
                    disabled={approveRefundMutation.isPending}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve Refund
                  </Button>
                  <Button
                    variant="outline"
                    className="border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                    onClick={() => handleManualComplete(refund.id)}
                    disabled={manualCompleteRefundMutation.isPending}
                  >
                    <ClipboardCheck className="h-4 w-4 mr-2" />
                    Manually Completed
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleReject(refund.id)}
                    disabled={rejectRefundMutation.isPending}
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedRefund(null);
                      setNotes('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 pt-4 border-t">
                <Button onClick={() => setSelectedRefund(refund.id)}>
                  Review & Approve
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      );
      })}

      <PaymentProcessingModal
        open={showRefundProcessingModal}
        amount={refundAmount}
        status={refundStatus}
        errorMessage={refundError}
        transactionId={stripeRefundId}
        onClose={() => {
          setShowRefundProcessingModal(false);
          setSelectedRefund(null);
          setNotes('');
          setProcessingRefundId(null);
        }}
        onRetry={handleRetryRefund}
      />

      {/* Manual Complete Dialog for Refunds */}
      <Dialog open={showManualCompleteDialog} onOpenChange={setShowManualCompleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Refund as Manually Completed</DialogTitle>
            <DialogDescription>
              This will mark the refund as completed without processing through Stripe. 
              The client will NOT receive funds through the platform. Use this only when 
              the refund was processed manually (e.g., check, bank wire).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="manual-refund-reason">Completion Reason (Required)</Label>
              <Textarea
                id="manual-refund-reason"
                placeholder="e.g., Refund issued via check #12345 on 01/15/2026"
                value={manualReason}
                onChange={(e) => setManualReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualCompleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmManualComplete}
              disabled={!manualReason.trim() || manualCompleteRefundMutation.isPending}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {manualCompleteRefundMutation.isPending ? 'Processing...' : 'Confirm Manual Completion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaymentHistorySection() {
  const { data: history, isLoading } = usePaymentHistory();
  const { data: platformFeeSetting } = usePlatformSetting('platform_fee_percentage');
  const configuredFeePercentage = parseFloat(platformFeeSetting?.setting_value || '7');
  const [filter, setFilter] = useState<'all' | 'payouts' | 'refunds'>('all');
  const [expandedTransactions, setExpandedTransactions] = useState<Record<string, boolean>>({});

  if (isLoading) {
    return <div>Loading payment history...</div>;
  }

  const payouts = history?.payouts || [];
  const refunds = history?.refunds || [];

  // Keep full payout objects for vendor payouts
  const payoutItems = payouts.map(p => ({
    type: 'payout' as const,
    id: p.id,
    jobId: p.job_id,
    jobPublicId: p.jobs?.public_id,
    jobTitle: p.jobs?.title,
    amount: p.display_vendor_payout_cents || p.payment_transactions?.vendor_payout_cents || 0,
    totalAmount: p.display_total_amount_cents || 0,
    platformFee: p.display_platform_fee_cents || p.aggregated_platform_fee_cents || 0,
    status: p.status,
    date: p.admin_approved_at || p.updated_at,
    approvedBy: p.admin_approved_by,
    notes: p.admin_approval_notes || p.rejection_reason,
    stripeId: p.payment_transactions?.stripe_transfer_id,
    isApproved: p.status === 'completed' || p.status === 'completed_manually',
    isRejected: p.status === 'failed_permanently',
    organizationName: (p.vendor_organization as any)?.name || 'Unknown Organization',
    hasReconciliation: p.has_reconciliation,
    reconciliation: p.reconciliation,
    transactionCount: p.transaction_count || 1,
  }));

  const refundItems = refunds.map(r => ({
    type: 'refund' as const,
    id: r.id,
    jobId: r.jobs?.id,
    jobPublicId: r.jobs?.public_id,
    jobTitle: r.jobs?.title,
    amount: r.refund_amount_cents,
    totalAmount: r.refund_amount_cents,
    platformFee: 0,
    status: r.status,
    date: r.approved_at || r.rejected_at || r.processed_at || r.created_at,
    approvedBy: r.approved_by || r.rejected_by,
    notes: r.approval_notes || r.rejection_reason,
    stripeId: r.stripe_refund_id,
    isApproved: ['approved', 'completed', 'completed_manually'].includes(r.status),
    isRejected: r.status === 'rejected',
    organizationName: (r.client_organization as any)?.name || 'Unknown Organization',
    hasReconciliation: false,
    reconciliation: undefined,
    transactionCount: 1,
  }));

  const allItems = [...payoutItems, ...refundItems]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const filteredItems = filter === 'all' 
    ? allItems 
    : allItems.filter(item => item.type === (filter === 'payouts' ? 'payout' : 'refund'));

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
        >
          All
        </Button>
        <Button
          variant={filter === 'payouts' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('payouts')}
        >
          Vendor Payouts
        </Button>
        <Button
          variant={filter === 'refunds' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('refunds')}
        >
          Client Refunds
        </Button>
      </div>

      {filteredItems.length === 0 ? (
        <Alert>
          <Clock className="h-4 w-4" />
          <AlertDescription>No payment history found.</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item) => {
            const isExpanded = expandedTransactions[item.id] || false;

            return (
              <Card key={`${item.type}-${item.id}`} className={item.isApproved ? 'border-l-4 border-l-green-500' : item.isRejected ? 'border-l-4 border-l-red-500' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <DollarSign className="h-5 w-5" />
                        {item.type === 'payout' ? 'Vendor Payout' : 'Client Refund'} - {item.jobPublicId}
                        {item.transactionCount > 1 && (
                          <Badge variant="secondary" className="ml-2">
                            {item.transactionCount} payments
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <Building className="h-4 w-4" />
                        {item.organizationName}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.isApproved && (
                        <Badge variant="outline" className="bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Approved
                        </Badge>
                      )}
                      {item.isRejected && (
                        <Badge variant="outline" className="bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800">
                          <XCircle className="h-3 w-3 mr-1" />
                          Rejected
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {item.jobTitle}
                  </p>

                  {/* Reconciliation Section - matching Vendor Payouts tab */}
                  {item.type === 'payout' && item.hasReconciliation && item.reconciliation && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary">Reconciliation Applied</Badge>
                        {item.reconciliation.adjustment_count > 0 && (
                          <span className="text-sm text-muted-foreground">
                            {item.reconciliation.adjustment_count} change order{item.reconciliation.adjustment_count > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Original PO:</span>
                          <span>${(item.reconciliation.original_po_amount_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        {item.reconciliation.total_increases_cents > 0 && (
                          <div className="flex justify-between text-green-600 dark:text-green-400">
                            <span>Increases:</span>
                            <span>+${(item.reconciliation.total_increases_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        {item.reconciliation.total_credits_cents > 0 && (
                          <div className="flex justify-between text-red-600 dark:text-red-400">
                            <span>Credits:</span>
                            <span>-${(item.reconciliation.total_credits_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-medium border-t pt-2">
                          <span>Final PO (Base, excl. tax):</span>
                          <span>${(item.reconciliation.final_po_amount_cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Payout Amount Section - matching Vendor Payouts tab */}
                  <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        {item.type === 'payout' ? 'Vendor Payout Amount (After Platform Fee)' : 'Refund Amount'}
                      </p>
                      {item.type === 'payout' && item.transactionCount > 1 && !item.hasReconciliation && (
                        <Badge variant="secondary">{item.transactionCount} transactions aggregated</Badge>
                      )}
                    </div>
                    <p className="text-3xl font-bold text-primary">
                      ${(item.amount / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                    {item.type === 'payout' && item.platformFee > 0 && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Platform Fee ({configuredFeePercentage}%): ${(item.platformFee / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    )}
                  </div>

                  {/* Transaction Breakdown for multi-transaction payouts */}
                  {item.type === 'payout' && item.transactionCount > 1 && item.jobId && (
                    <TransactionBreakdown 
                      jobId={item.jobId} 
                      isExpanded={isExpanded} 
                      onToggle={() => setExpandedTransactions(prev => ({ ...prev, [item.id]: !isExpanded }))} 
                    />
                  )}

                  {/* Job Ledger View */}
                  {item.type === 'payout' && item.jobId && (
                    <JobLedgerView jobId={item.jobId} />
                  )}

                  <div className="pt-4 border-t grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground">Status</p>
                      <p className="font-medium">{item.status}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Date</p>
                      <p className="font-medium">{format(new Date(item.date), 'PPp')}</p>
                    </div>
                    {item.stripeId && (
                      <div>
                        <p className="text-muted-foreground">Stripe Reference</p>
                        <p className="font-mono text-xs">{item.stripeId}</p>
                      </div>
                    )}
                    {item.notes && (
                      <div className="col-span-2">
                        <p className="text-muted-foreground">Notes</p>
                        <p className="text-sm">{item.notes}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

